import * as duckdb from "@duckdb/duckdb-wasm";
import {
  AsnEntry, appsGroupFilterResource, ComboboxSelectionItem,
  CountryEntry, DCNetscopeResource, DCNetscopeResourceWithName, DuckDbAppsGroup, DuckDbEmitterDescription,
  DuckDbProtocolRatioDescription,
  DuckDbResourceObject,
  FlowDetail,
  FlowRessourceObject,
  GraphDataObject, ResourceProtocolData, SourceResourceProtocolData
} from "@app/netscope/model/data-models";
import {Observable} from "rxjs";
import {tableToIPC} from "apache-arrow";
import * as aq from "arquero";
import {ColumnTable} from "arquero";
import {ClrDatagridStateInterface} from "@clr/angular";
import {NetscopeUtils} from "@app/netscope/netscope-utils";
import {EventEmitter} from "@angular/core";
import {
  AsnFlow,
  CountryFlow, ResourceFlow,
  VmWithPortsAccessedFromOutsideInfo
} from "@app/netscope/netscope-outside-flows/netscope-outside-flows.component";
import {NetscopeApiService, Success} from "@app/services/netscope-api.service";
import { firstValueFrom } from 'rxjs';

export class ProtocolAnalysisSelection {
  label: string;
  whereClause: string;
  selectedColumn: string;
}

export class RangeBoundaries {
  min: number;
  max: number;
}

export class DashboardDataResponse {
    amountOfDataProcessed: number;
    amountOfPacketsProcessed: number;
    amountOfFlowsProcessed: number;
}

export class InsideOutsideResponse {
    inside: DashboardDataResponse;
    outside: DashboardDataResponse;
}

export class OutsideFlowsComponentData {
    outside: DashboardDataResponse;
    resourceCount: number;
    resourceRanking: Map<string, ResourceFlow>;
    resourceAccessibleFromOutsideRanking: Map<string, ResourceFlow>;
    countryRanking: Map<string, CountryFlow>;
    asnRanking: Map<string, AsnFlow>;
    vmsWithPortsAccessedFromOutside: VmWithPortsAccessedFromOutsideInfo[];
}

export class SrcTargetResourceTypeData {
  src_resource_type: string;
  dst_resource_type: string;
  exchanged_bytes: number;
  exchanged_packets: number;
  count: number;
}

export class NetworkViewerData {
  src_ipaddr: string;
  src_resource_type: string;
  dst_ipaddr: string;
  dst_resource_type: string;
  src_country_code: string;
  src_asn_num: string;
  dst_country_code: string;
  dst_asn_num: string;

  exchanged_bytes: number;
  exchanged_packets: number;
}

export class AsnCountryMaps {
  asnMap: Map<number, AsnEntry> = new Map<number, AsnEntry>();
  countryMap: Map<string, CountryEntry> = new Map<string, CountryEntry>();
}

// Flows classified by the flows descriptions, each repository reading its share of the same classified files
export class FlowsClassifiedByDescriptions {
  described: FlowsRepository;
  authorized: FlowsRepository;
  unauthorized: FlowsRepository;
  undescribed: FlowsRepository;
}

export class DescriptionCoverage {
  description_index: number;
  flows_count: number;
  exchanged_bytes: number;
}

// Temporary files are written once and read a few times: a light compression keeps their writing cheap
const TEMPORARY_FILES_COMPRESSION = ", COMPRESSION zstd, COMPRESSION_LEVEL 1";

////////////////////////////////////////
//
// FlowsRepository
//
//
//
//
////////////////////////////////////////

export abstract class FlowsRepository {

  protected conn: duckdb.AsyncDuckDBConnection;
  whereClause: string;
  initWhereClause: string;

  nodeMap: Map<string, FlowRessourceObject>;
  countryMap: Map<string, CountryEntry>;
  asnMap: Map<number, AsnEntry>;
  duckDbNodeMapping: Map<string, DuckDbResourceObject>;

  allFlowDetails: FlowDetail[] = [];
  dashboardFlows: FlowDetail[] = [];
  asyncFlows$: Observable<FlowDetail>;
  totalItemsCount = 0;
  totalExchangedBytes = 0;
  internalUuid = "";

  aggregated_flows_files = "aggregated_file.parquet"
  flows_files = "file.parquet"
  // When set, the repository only reads the flows of its files matching this clause, the files being shared
  flowsSubsetClause = "";
  firstPageShown = false;

  datagridFilterMap: Map<string, string>;


  // This is necessary as ngx-slider does not support well "async init_filter" functions. It works sometimes, but often
  // times it breaks without any reason. This is why we pre-compute values used by RangeFilters (based on ngx-slider)
  valuesForFilters = {
    exchanged_bytes: {
      min: 0,
      max: 0
    },
    exchanged_packets: {
      min: 0,
      max: 0
    }
  }

  isLoading: boolean = false;

  repositoryHasBeenUpdated: EventEmitter < any > = new EventEmitter();

  constructor(conn: duckdb.AsyncDuckDBConnection, whereClause: string, nodeMap: Map<string, FlowRessourceObject>, countryMap: Map<string, CountryEntry>, asnMap: Map<number, AsnEntry>, duckDbNodeMapping: Map<string, DuckDbResourceObject>) {
    this.conn = conn;
    this.whereClause = whereClause;

    this.nodeMap = nodeMap;

    this.countryMap = countryMap;
    this.asnMap = asnMap;

    this.duckDbNodeMapping = duckDbNodeMapping;
    this.internalUuid = NetscopeUtils.generateUuid();

    this.firstPageShown = false;
    this.datagridFilterMap = new Map();
  }

  abstract executeQueryFastArquaro(conn, query): Promise<any[]>;

  getDatabaseLocations = () => ({
    "raw": `read_parquet('file.parquet')`,
    "aggregatedRaw": `read_parquet('aggregated_file.parquet')`,
    "flowsDb": this.subsetOf(`read_parquet('${this.flows_files}', hive_partitioning = true)`),
    "aggregatedFlowsDb": this.subsetOf(`read_parquet('${this.aggregated_flows_files}', hive_partitioning = true)`),
  })

  // A location restricted to the flows of the repository, when its files are shared with other repositories
  protected subsetOf = (location: string) => {
    if (this.flowsSubsetClause == "") {
      return location;
    }
    return `(select * from ${location} where ${this.flowsSubsetClause})`;
  }

  generateNewFilepaths = () => {
    let internalUuid = NetscopeUtils.generateUuid();

    let flows_files = `${internalUuid}_file`;
    let aggregated_flows_files = `${internalUuid}_file_aggregated`;

    return {
      internalUuid: internalUuid,
      flows_files: flows_files,
      aggregated_flows_files: aggregated_flows_files,
    }
  }

  // Query writing into 'target' the flows of 'source' aggregated per pair of resources, and per 'groupColumns' if
  // any. 'extraColumns' are additional aggregate expressions, written with their alias.
  aggregatedFlowsCopyQuery = (source: string, target: string, filter = "", groupColumns: string[] = [],
                              extraColumns: string[] = []) => {
    let selectedColumns = [...groupColumns, ...extraColumns].map((c) => `${c},`).join("\n              ");
    let groupColumnsClause = groupColumns.map((c) => `, ${c}`).join("");

    return `
        COPY (
          select
              ${selectedColumns}
              src_resource_uuid,
              dst_resource_uuid,
              ANY_VALUE(src_ipaddr) as src_ipaddr,
              ANY_VALUE(src_resource_type) as src_resource_type,
              ANY_VALUE(dst_ipaddr) as dst_ipaddr,
              ANY_VALUE(dst_resource_type) as dst_resource_type,

              sum(exchanged_packets) as exchanged_packets,
              sum(exchanged_bytes) as exchanged_bytes,
              ANY_VALUE(direction) as direction,

              ANY_VALUE(src_country_code) as src_country_code,
              ANY_VALUE(src_asn_num) as src_asn_num,
              ANY_VALUE(src_asn_name) as src_asn_name,
              ANY_VALUE(dst_country_code) as dst_country_code,
              ANY_VALUE(dst_asn_num) as dst_asn_num,
              ANY_VALUE(dst_asn_name) as dst_asn_name,

          from ${source}
          ${filter}
          group by src_resource_uuid, dst_resource_uuid${groupColumnsClause}
          order by exchanged_bytes DESC
        ) TO '${target}' (FORMAT PARQUET ${TEMPORARY_FILES_COMPRESSION});
      `;
  }

  async init(filter = "") {
    let dbLocations = this.getDatabaseLocations();

    this.isLoading = true;
    this.initWhereClause = filter;

    // if (filter != "" && filter != "where true and true and true") {

    if (filter.includes("protocol =") || filter.includes("port =") || filter.includes("protocol !=") || filter.includes("port !=")) {
      // A repository reading a share of classified files keeps to its share, the others start from all the flows
      let source = this.flowsSubsetClause == "" ? dbLocations.raw : dbLocations.flowsDb;

      let newFilepaths = this.generateNewFilepaths();

      this.internalUuid = newFilepaths.internalUuid;
      this.flows_files = newFilepaths.flows_files;
      this.aggregated_flows_files = newFilepaths.aggregated_flows_files;
      this.flowsSubsetClause = "";

      // First generate a file of all ports, following the filter
      let query = `
        COPY (
          select *
          from ${source}
          ${filter}
        ) TO '${this.flows_files}' (FORMAT PARQUET ${TEMPORARY_FILES_COMPRESSION});
      `;

      console.time(`[flowsRepository] init raw flows (${filter})`);
      await this.executeQueryFastArquaro(this.conn, query);
      console.timeEnd(`[flowsRepository] init raw flows (${filter})`);

      // Second, aggregate the file that was just generated: it already follows the filter
      dbLocations = this.getDatabaseLocations();
      query = this.aggregatedFlowsCopyQuery(dbLocations.flowsDb, this.aggregated_flows_files);

      console.time(`[flowsRepository] init aggregated flows (${filter})`);
      await this.executeQueryFastArquaro(this.conn, query);
      console.timeEnd(`[flowsRepository] init aggregated flows (${filter})`);
      this.whereClause = "where true";
    } else {
      this.whereClause = filter;
    }

    await this.computeValuesForFilters();
    this.repositoryHasBeenUpdated.emit();
    this.isLoading = false;
  }

  /**
   * Classifies all the flows by the descriptions matching them, evaluating each description once per flow:
   *  - 'matched_descriptions' lists the indexes of the descriptions whose clause matches the flow,
   *  - 'description_category' is 'unauthorized' when one of them is unauthorized, 'authorized' when some match,
   *    and 'undescribed' when none does.
   * The classified flows are written once, each returned repository reading its share of them.
   */
  classifyByDescriptions = async (descriptionsClauses: string[], unauthorizedIndexes: number[]): Promise<FlowsClassifiedByDescriptions> => {
    let dbLocations = this.getDatabaseLocations();
    let newFilepaths = this.generateNewFilepaths();

    let classifiedFlowsFile = newFilepaths.flows_files;
    let aggregatedFile = newFilepaths.aggregated_flows_files;
    let describedAggregatedFile = `${newFilepaths.aggregated_flows_files}_described`;

    let matchingIndexes = descriptionsClauses
      .map((clause, index) => `CASE WHEN (${clause}) THEN ${index} END`)
      .join(",\n              ");
    let unauthorizedMatch = unauthorizedIndexes.length == 0
      ? "false"
      : `list_has_any(matched_descriptions, [${unauthorizedIndexes.join(", ")}]::INTEGER[])`;

    let query = `
      COPY (
        select *,
          CASE
            WHEN ${unauthorizedMatch} THEN 'unauthorized'
            WHEN len(matched_descriptions) > 0 THEN 'authorized'
            ELSE 'undescribed'
          END as description_category
        from (
          select *,
            list_filter([
              ${matchingIndexes}
            ]::INTEGER[], lambda x: x IS NOT NULL) as matched_descriptions
          from ${dbLocations.raw}
        )
      ) TO '${classifiedFlowsFile}' (FORMAT PARQUET ${TEMPORARY_FILES_COMPRESSION});
    `;

    console.time("[flowsRepository] classify flows by descriptions");
    await this.executeQueryFastArquaro(this.conn, query);
    console.timeEnd("[flowsRepository] classify flows by descriptions");

    // The described flows are aggregated apart: a pair of resources may have both authorized and unauthorized flows.
    // Such a pair is unauthorized, so that the file holds the category the subset clause of the repository reads.
    let classifiedFlowsDb = `read_parquet('${classifiedFlowsFile}', hive_partitioning = true)`;

    console.time("[flowsRepository] aggregate classified flows");
    await this.executeQueryFastArquaro(this.conn,
      this.aggregatedFlowsCopyQuery(classifiedFlowsDb, aggregatedFile, "", ["description_category"]));
    await this.executeQueryFastArquaro(this.conn,
      this.aggregatedFlowsCopyQuery(classifiedFlowsDb, describedAggregatedFile, "where description_category != 'undescribed'", [],
        ["CASE WHEN bool_or(description_category = 'unauthorized') THEN 'unauthorized' ELSE 'authorized' END as description_category"]));
    console.timeEnd("[flowsRepository] aggregate classified flows");

    let share = async (aggregatedFlowsFiles: string, subsetClause: string) => {
      let repository = this.clone();
      repository.flows_files = classifiedFlowsFile;
      repository.aggregated_flows_files = aggregatedFlowsFiles;
      repository.flowsSubsetClause = subsetClause;
      repository.whereClause = "where true";
      repository.initWhereClause = "where true";
      await repository.computeValuesForFilters();
      return repository;
    };

    let [described, authorized, unauthorized, undescribed] = await Promise.all([
      share(describedAggregatedFile, "description_category != 'undescribed'"),
      share(aggregatedFile, "description_category = 'authorized'"),
      share(aggregatedFile, "description_category = 'unauthorized'"),
      share(aggregatedFile, "description_category = 'undescribed'"),
    ]);

    return {described, authorized, unauthorized, undescribed};
  }

  // For each description matching some flows of the repository, the pairs of resources and the traffic it covers
  getDescriptionsCoverage = async (): Promise<DescriptionCoverage[]> => {
    let dbLocations = this.getDatabaseLocations();
    let whereClause = this.generateWhereClause();

    let query = `
      select
        description_index,
        count(distinct (src_resource_uuid, dst_resource_uuid)) as flows_count,
        sum(exchanged_bytes) as exchanged_bytes
      from (
        select unnest(matched_descriptions) as description_index, src_resource_uuid, dst_resource_uuid, exchanged_bytes
        from ${dbLocations.flowsDb}
        ${whereClause}
      )
      group by description_index
    `;

    return await this.executeQueryFastArquaro(this.conn, query);
  }

  computeValuesForFilters = async () => {
    let exchanged_bytes_boundaries = await this.getRangeBoundaries("exchanged_bytes");
    let exchanged_packets_boundaries = await this.getRangeBoundaries("exchanged_packets");

    this.valuesForFilters.exchanged_bytes.min = exchanged_bytes_boundaries.min;
    this.valuesForFilters.exchanged_bytes.max = exchanged_bytes_boundaries.max;

    this.valuesForFilters.exchanged_packets.min = exchanged_packets_boundaries.min;
    this.valuesForFilters.exchanged_packets.max = exchanged_packets_boundaries.max;
  }

  showFirstPage = () => {
    this.firstPageShown = true;
    this.refresh({
      page: {
        size: 20,
        current: 1
      }
    })
  }

  refresh = async (state: ClrDatagridStateInterface) => {

    let dbLocations = this.getDatabaseLocations();

    this.isLoading = true;
    // this.loading = true;
    const filters: { [prop: string]: any[] } = {};
    if (state.filters) {
      for (const filter of state.filters) {
        const {property, value} = <{ property: string; value: string }>filter;
        filters[property] = [value];
      }
    }

    let sortClause = "";
    if (state.sort != undefined) {

      sortClause = `order by ${state.sort.by} ${state.sort.reverse ? 'DESC' : 'ASC'}`;
      // console.log("ici");
    }

    const pageSize = state.page?.size || 20;
    const currentPage = state.page?.current || 1;

    let whereClause = this.generateWhereClause();

    let allMatchingRowsCountQuery = `
      SELECT count(*) as flows_count
      from ${dbLocations.aggregatedFlowsDb}
      ${whereClause}
    `;

    console.time(`[flowsRepository] reset (count) (${whereClause})`);
    let flowsCountResult = await this.executeQueryFastArquaro(this.conn, allMatchingRowsCountQuery);
    console.timeEnd(`[flowsRepository] reset (count) (${whereClause})`);
    let flowsCount = flowsCountResult[0]["flows_count"]

    let exchangedBytesColumn = "exchanged_bytes";
    let exchangedPacketsColumn = "exchanged_packets";
    let additionalColumns = "";

    if (this.aggregated_flows_files == this.flows_files) {
      exchangedBytesColumn = "exchanged_bytes as exchanged_bytes";
      exchangedPacketsColumn = "exchanged_packets as exchanged_packets";
      additionalColumns = `
        dst_port as port,
        direction,
        protocol,
        proto_raw,
      `;
    }

    let fetchAllFlowsQuery = `
        select
          src_ipaddr,
          src_resource_uuid,
          src_resource_type,
          dst_ipaddr,
          dst_resource_uuid,
          dst_resource_type,
          ${additionalColumns}
          ${exchangedPacketsColumn},
          ${exchangedBytesColumn},
          src_country_code,
          src_asn_num,
          dst_country_code,
          dst_asn_num
        from ${dbLocations.aggregatedFlowsDb}
        ${whereClause}
        ${sortClause}
        LIMIT ${pageSize} OFFSET ${(currentPage - 1) * pageSize}
    `;
    console.time("[flowsRepository] refresh (fastArquara)");
    let allFlowsWithPortsAndProtocolsFastBatch = await this.executeQueryFastArquaro(this.conn, fetchAllFlowsQuery);
    console.timeEnd("[flowsRepository] refresh (fastArquara)");

    console.log("building list of flows with ports and protocols");

    let skipMetrics = true;
    if (this.aggregated_flows_files == this.flows_files) {
      skipMetrics = false;
    }

    console.time("[flowsRepository] refresh (duckDbFlowsToFlowsDetails)");
    let flowDetailsWithPortsAndProtocols = NetscopeUtils.duckDbFlowsToFlowsDetails(allFlowsWithPortsAndProtocolsFastBatch, this.nodeMap, this.countryMap, this.asnMap, this.duckDbNodeMapping, false, skipMetrics);
    console.timeEnd("[flowsRepository] refresh (duckDbFlowsToFlowsDetails)");

    if (! skipMetrics) {
      console.time("[flowsRepository] propagating ports");
      for (let flow of flowDetailsWithPortsAndProtocols) {
        let metric = flow.metrics.values().next().value;
        flow.direction = metric.direction;
        flow.port = metric.port;
        flow.protocol = metric.application_protocol;
        flow.transport_protocols = metric.transport_protocols;
      }
      console.timeEnd("[flowsRepository] propagating ports");
    }

    console.time("[flowsRepository] refresh (sorting flowDetailsWithPortsAndProtocols)");
    this.allFlowDetails = flowDetailsWithPortsAndProtocols;

    if (sortClause == "") {
      this.allFlowDetails = this.allFlowDetails.sort((fa, fb) => fb.exchanged_bytes - fa.exchanged_bytes);
    }
    this.totalItemsCount = this.allFlowDetails.length;
    this.totalExchangedBytes = 0;
    for (let flow of this.allFlowDetails) {
      this.totalExchangedBytes += flow.exchanged_bytes;
    }
    console.timeEnd("[flowsRepository] refresh (sorting flowDetailsWithPortsAndProtocols)");

    // Init dashboard flows
    this.dashboardFlows = this.allFlowDetails;
    this.totalItemsCount = flowsCount;

    // Trick to smooth disappearance of loading spinner
    setTimeout(() => {
      this.isLoading = false;
    }, 200);
  }

  setWhereClause = (newClause: string) => {
    // this.whereClause = newClause;
    let whereClauseAsFilter = newClause.replace("where", "");
    this.datagridFilterMap.set("default", whereClauseAsFilter);

    this.computeValuesForFilters().then(() => {
      this.repositoryHasBeenUpdated.emit();
    });
  }

  generateWhereClause = () => {
    let individualWhereExpressions = Array.from(this.datagridFilterMap.values());

    let initialWhereClause = this.whereClause.replaceAll("where ", "");
    individualWhereExpressions.push(initialWhereClause);

    if (individualWhereExpressions.length == 0) {
      return "where true";
    }

    let combinedWhereExpressions = individualWhereExpressions
      .map((e) => `(${e})`)
      .join(" and ");
    let whereClause = `where ${combinedWhereExpressions}`;
    return whereClause;
  }

  abstract clone(): FlowsRepository;

  generateGraphData = async (withProtocols=false) => {
    let dbLocations = this.getDatabaseLocations();
    let result: GraphDataObject = new GraphDataObject();

    let whereClause = this.generateWhereClause();


    let additionalColummns = ""
    let additionalGroupClause = ""
    let table = withProtocols ? dbLocations.flowsDb : dbLocations.aggregatedFlowsDb;

    let getLinksQuery = `
      SELECT
        ${additionalColummns}
        src_resource_uuid,
        dst_resource_uuid,
        src_resource_type,
        dst_resource_type,
        src_ipaddr,
        dst_ipaddr,
        src_asn_num,
        dst_asn_num,
        src_country_code,
        dst_country_code,
        exchanged_bytes,
        exchanged_packets
      from ${table}
      ${whereClause}
      ${additionalGroupClause}
    `;

    if (withProtocols) {
      additionalColummns = "protocol, dst_port as port, direction,"
      additionalGroupClause = "group by protocol, port, direction, src_resource_uuid,\n        dst_resource_uuid,\n        src_resource_type,\n        dst_resource_type,\n        src_ipaddr,\n        dst_ipaddr,\n        src_asn_num, dst_asn_num, src_country_code, dst_country_code"
      // table = dbLocations.flowsDb;

      getLinksQuery = `
        SELECT
          protocol, direction,
          src_resource_uuid,
          dst_resource_uuid,
          src_resource_type,
          dst_resource_type,
          src_ipaddr,
          dst_ipaddr,
          src_asn_num,
          dst_asn_num,
          src_country_code,
          dst_country_code,
          sum(exchanged_bytes) as exchanged_bytes,
          sum(exchanged_packets) as exchanged_packets
        from ${table}
        ${whereClause}
        group by protocol, direction,
          src_resource_uuid,
          dst_resource_uuid,
          src_resource_type,
          dst_resource_type,
          src_ipaddr,
          dst_ipaddr,
          src_asn_num,
          dst_asn_num,
          src_country_code,
          dst_country_code
    `;
    }


    console.time("[flowsRepository] generateGraphData (fetchLinksFromDuckDB)");
    let allFlowsWithPortsAndProtocols = await this.executeQueryFastArquaro(this.conn, getLinksQuery);
    console.timeEnd("[flowsRepository] generateGraphData (fetchLinksFromDuckDB)");

    console.time("[flowsRepository] generateGraphData (processing links)");
    let flowDetailsWithPortsAndProtocols = NetscopeUtils.duckDbFlowsToFlowsDetails(allFlowsWithPortsAndProtocols, this.nodeMap, this.countryMap, this.asnMap, this.duckDbNodeMapping, false, !withProtocols);
    console.timeEnd("[flowsRepository] generateGraphData (processing links)");

    // find nodes
    console.time("[flowsRepository] generateGraphData (build nodes list)");
    let nodes = Array.from(this.nodeMap.values());
    console.timeEnd("[flowsRepository] generateGraphData (build nodes list)");

    result.flowsRepository = this;
    result.links = flowDetailsWithPortsAndProtocols;
    result.nodes = nodes;

    return result;
  }

  /**
   * Aggregates the flows per pair of resources, the source and the destination as the flows hold them, each
   * column being an aggregate expression evaluated over the flows of the pair ('bool_or(...)', 'sum(...)'). The
   * flows are the ones generateGraphData reads, so that each pair matches one of its links.
   */
  aggregateFlowsPairs = async (columns: {alias: string, expression: string}[]): Promise<any[]> => {
    let dbLocations = this.getDatabaseLocations();
    let whereClause = this.generateWhereClause();

    let selectedColumns = columns
      .map((column) => `${column.expression} as ${column.alias}`)
      .join(",\n        ");

    let query = `
      SELECT
        src_resource_uuid,
        dst_resource_uuid${columns.length > 0 ? "," : ""}
        ${selectedColumns}
      from ${dbLocations.flowsDb}
      ${whereClause}
      group by src_resource_uuid, dst_resource_uuid
    `;

    return await this.executeQueryFastArquaro(this.conn, query);
  }

  async getFlowsMetrics(srcResourceUuid: string, dstResourceUuid: string) {
    let where = `where src_resource_uuid = '${srcResourceUuid}' and dst_resource_uuid = '${dstResourceUuid}'`;
    return this.getFlowsMetricsWhere(where);
  }

  async getFlowsMetricsWhere(where_clause: string) {
    let dbLocations = this.getDatabaseLocations();

    let repositoryWhereClauseFiltered = this.whereClause.replaceAll("where", "");
    if (repositoryWhereClauseFiltered == "") {
      repositoryWhereClauseFiltered = " true ";
    }

    let fetchAllFlowsQuery = `
        select
          src_ipaddr,
          src_resource_uuid,
          src_resource_type,
          dst_ipaddr,
          dst_resource_uuid,
          dst_resource_type,
          dst_port as port,
          direction,
          protocol,
          proto_raw,
          exchanged_packets,
          exchanged_bytes,
          src_country_code,
          src_asn_num,
          dst_country_code,
          dst_asn_num
        from ${dbLocations.flowsDb}
        ${where_clause} and (${repositoryWhereClauseFiltered})
    `;
    console.time("[flowsRepository] getFlowsMetricsWhere (fastArquara)");
    let allFlowsWithPortsAndProtocolsFastBatch = await this.executeQueryFastArquaro(this.conn, fetchAllFlowsQuery);
    console.timeEnd("[flowsRepository] getFlowsMetricsWhere (fastArquara)");

    console.time("[flowsRepository] getFlowsMetricsWhere (processing links)");
    let flowDetailsWithPortsAndProtocols = NetscopeUtils.duckDbFlowsToFlowsDetails(allFlowsWithPortsAndProtocolsFastBatch, this.nodeMap, this.countryMap, this.asnMap, this.duckDbNodeMapping, false, false);
    console.timeEnd("[flowsRepository] getFlowsMetricsWhere (processing links)");

    return flowDetailsWithPortsAndProtocols;
  }

  async getAllDiscreteValues(columns: string[]) {
    let dbLocations = this.getDatabaseLocations();
    let columnsAsStr = columns.join(", ");

    let dbFile = dbLocations.aggregatedFlowsDb;
    if (columns.includes("protocol")) {
      dbFile = dbLocations.flowsDb;
    }

    let fetchAllDistinctValuesQuery = `
        select
          ${columnsAsStr}
        from ${dbFile}
        group by ${columnsAsStr}
    `;
    console.time(`[flowsRepository] get distinct values for '${columnsAsStr}' (fastArquara)`);
    let allFlowsWithPortsAndProtocolsFastBatch = await this.executeQueryFastArquaro(this.conn, fetchAllDistinctValuesQuery);
    console.timeEnd(`[flowsRepository] get distinct values for '${columnsAsStr}' (fastArquara)`);

    return allFlowsWithPortsAndProtocolsFastBatch;
  }

  async getRangeBoundaries(column: string) {
    let dbLocations = this.getDatabaseLocations();
    let whereClause = this.generateWhereClause();

    let selectedDatabase = dbLocations.aggregatedFlowsDb;
    if (whereClause.includes("dst_port")) {
      selectedDatabase = dbLocations.flowsDb;
    }

    let fetchAllDistinctValuesQuery = `
        select
          min(${column}) as min_value,
          max(${column}) as max_value
        from ${selectedDatabase}
        ${whereClause}
    `;
    console.time(`[flowsRepository] getRangeBoundaries '${column}' (fastArquara)`);
    let minMaxValues = await this.executeQueryFastArquaro(this.conn, fetchAllDistinctValuesQuery);
    console.timeEnd(`[flowsRepository] getRangeBoundaries '${column}' (fastArquara)`);

    let result = new RangeBoundaries();
    result.min = minMaxValues[0]["min_value"];
    result.max = minMaxValues[0]["max_value"];

    return result;
  }

  getDashboardData = async () => {
    let dbLocations = this.getDatabaseLocations();
    let whereClause = this.generateWhereClause();

    let sqlQuery = `
      select
          sum(exchanged_bytes) as amountOfDataProcessed,
          sum(exchanged_packets) as amountOfPacketsProcessed,
          count(*) as amountOfFlowsProcessed
        from ${dbLocations.aggregatedFlowsDb}
        ${whereClause}
    `;
    console.time(`[flowsRepository] getDashboardData (fastArquara)`);
    let dbValues = await this.executeQueryFastArquaro(this.conn, sqlQuery);
    console.timeEnd(`[flowsRepository] getDashboardData (fastArquara)`);

    let result: DashboardDataResponse = {
      amountOfDataProcessed: dbValues[0]["amountOfDataProcessed"],
      amountOfPacketsProcessed: dbValues[0]["amountOfPacketsProcessed"],
      amountOfFlowsProcessed: dbValues[0]["amountOfFlowsProcessed"]
    }

    return result;
  }

  getInsideOutsideRatioData = async () => {
    let dbLocations = this.getDatabaseLocations();
    let whereClause = this.generateWhereClause();

    let sqlQueryOutside = `
      select
          sum(exchanged_bytes) as amountOfDataProcessed,
          sum(exchanged_packets) as amountOfPacketsProcessed,
          count(*) as amountOfFlowsProcessed
        from ${dbLocations.aggregatedFlowsDb}
        ${whereClause} and (src_resource_type = 'internet_ip' or dst_resource_type = 'internet_ip')
    `;
    console.time(`[flowsRepository] getDashboardData (outside) (fastArquara)`);
    let outsideDashboardValues = await this.executeQueryFastArquaro(this.conn, sqlQueryOutside);
    console.timeEnd(`[flowsRepository] getDashboardData (outside) (fastArquara)`);

    let sqlQueryInside = `
      select
          sum(exchanged_bytes) as amountOfDataProcessed,
          sum(exchanged_packets) as amountOfPacketsProcessed,
          count(*) as amountOfFlowsProcessed
        from ${dbLocations.aggregatedFlowsDb}
        ${whereClause} and not (src_resource_type = 'internet_ip' or dst_resource_type = 'internet_ip')
    `;
    console.time(`[flowsRepository] getDashboardData (inside) (fastArquara)`);
    let insideDashboardValues = await this.executeQueryFastArquaro(this.conn, sqlQueryInside);
    console.timeEnd(`[flowsRepository] getDashboardData (inside) (fastArquara)`);

    let result: InsideOutsideResponse = {
      inside: {
        amountOfDataProcessed: insideDashboardValues[0]["amountOfDataProcessed"],
        amountOfPacketsProcessed: insideDashboardValues[0]["amountOfPacketsProcessed"],
        amountOfFlowsProcessed: insideDashboardValues[0]["amountOfFlowsProcessed"]
      },
      outside: {
        amountOfDataProcessed: outsideDashboardValues[0]["amountOfDataProcessed"],
        amountOfPacketsProcessed: outsideDashboardValues[0]["amountOfPacketsProcessed"],
        amountOfFlowsProcessed: outsideDashboardValues[0]["amountOfFlowsProcessed"]
      }
    };

    return result;
  }

  getOutsideFlowsComponentData = async () => {
    let dbLocations = this.getDatabaseLocations();
    let whereClause = this.generateWhereClause();

    // Compute volumetry for outside flows
    let sqlQueryOutside = `
      select
          sum(exchanged_bytes) as amountOfDataProcessed,
          sum(exchanged_packets) as amountOfPacketsProcessed,
          count(*) as amountOfFlowsProcessed
        from ${dbLocations.aggregatedFlowsDb}
        ${whereClause} and (src_resource_type = 'internet_ip' or dst_resource_type = 'internet_ip')
    `;
    console.time(`[flowsRepository] getDashboardData (outside) (fastArquara)`);
    let outsideDashboardValues = await this.executeQueryFastArquaro(this.conn, sqlQueryOutside);
    console.timeEnd(`[flowsRepository] getDashboardData (outside) (fastArquara)`);

    // Compute how many resources appear
    let sqlUniqResourceCount = `
      select count(*) as count
      from
        (
          select src_resource_uuid as uuid
        from ${dbLocations.aggregatedFlowsDb}
          ${whereClause} and not (src_resource_type = 'internet_ip' or dst_resource_type = 'internet_ip')

          UNION

          select dst_resource_uuid as uuid
        from ${dbLocations.aggregatedFlowsDb}
          ${whereClause} and not (src_resource_type = 'internet_ip' or dst_resource_type = 'internet_ip')
      ) fp
    `;
    console.time(`[flowsRepository] getDashboardData (resourceCount) (fastArquara)`);
    let resourcesCount = await this.executeQueryFastArquaro(this.conn, sqlUniqResourceCount);
    console.timeEnd(`[flowsRepository] getDashboardData (resourceCount) (fastArquara)`);

    // Compute countries rankings
    let sqlCountryCount = `
      select
        country_code,
        count(*) as count,
        sum(exchanged_bytes) as exchanged_bytes,
        sum(exchanged_packets) as exchanged_packets
      from
        (
          (select src_country_code as country_code,
          exchanged_bytes,
          exchanged_packets
        from ${dbLocations.aggregatedFlowsDb}
          ${whereClause})

          UNION

          (select dst_country_code as country_code,
          exchanged_bytes,
          exchanged_packets
        from ${dbLocations.aggregatedFlowsDb}
          ${whereClause})
      ) fp
      where country_code is not NULL
      group by country_code
    `;
    console.time(`[flowsRepository] getDashboardData (countryCount) (fastArquara)`);
    let countryCounts = await this.executeQueryFastArquaro(this.conn, sqlCountryCount);
    console.timeEnd(`[flowsRepository] getDashboardData (countryCount) (fastArquara)`);

    let countryRanking = new Map<string, CountryFlow>();
    for (let countryCount of countryCounts) {
      let countryFromDuckDb = this.countryMap.get(countryCount.country_code);

      if (countryFromDuckDb == undefined) {
        continue;
      }

      let countryObject: CountryFlow = {
        country: countryFromDuckDb.country,
        countryCode: countryCount.country_code,
        exchanged_bytes: countryCount.exchanged_bytes,
        exchanged_packets: countryCount.exchanged_packets,
        count: countryCount.count
      }
      countryRanking.set(countryCount.country_code, countryObject);
    }

    // Compute asn rankings
    let sqlAsnCount = `
      select
        asn_num,
        count(*) as count,
        sum(exchanged_bytes) as exchanged_bytes,
        sum(exchanged_packets) as exchanged_packets
      from
        (
          (select src_asn_num as asn_num,
          exchanged_bytes,
          exchanged_packets
            from ${dbLocations.aggregatedFlowsDb}
          ${whereClause})

          UNION

          (select dst_asn_num as asn_num,
          exchanged_bytes,
          exchanged_packets
            from ${dbLocations.aggregatedFlowsDb}
          ${whereClause})
      ) fp
      where asn_num is not NULL
      group by asn_num
    `;
    console.time(`[flowsRepository] getDashboardData (asnCount) (fastArquara)`);
    let asnCounts = await this.executeQueryFastArquaro(this.conn, sqlAsnCount);
    console.timeEnd(`[flowsRepository] getDashboardData (asnCount) (fastArquara)`);

    let asnRanking = new Map<string, AsnFlow>();
    for (let asnCount of asnCounts) {
      let asnFromDuckDb = this.asnMap.get(asnCount.asn_num);

      let asnObject: AsnFlow = {
        network: asnFromDuckDb.asn,
        asnNum: asnCount.asn_num,
        exchanged_bytes: asnCount.exchanged_bytes,
        exchanged_packets: asnCount.exchanged_packets,
        count: asnCount.count
      }
      asnRanking.set(asnCount.asn_num, asnObject);
    }

    // Compute resource rankings
    let sqlResourceCount = `
      select
        resource_uuid,
        ANY_VALUE(ipaddr) as ipaddr,
        ANY_VALUE(resource_type) as resource_type,
        ANY_VALUE(asn_num) as asn_num,
        ANY_VALUE(country_code) as country_code,
        count(*) as count,
        sum(exchanged_bytes) as exchanged_bytes,
        sum(exchanged_packets) as exchanged_packets
      from
        (
          (select
            src_resource_uuid as resource_uuid,
            src_ipaddr as ipaddr,
            src_resource_type as resource_type,
            src_country_code as country_code,
            src_asn_num as asn_num,
            exchanged_bytes,
            exchanged_packets
                from ${dbLocations.aggregatedFlowsDb}
          ${whereClause} and (src_resource_type != 'internet_ip' or dst_resource_type = 'internet_ip')
          )

          UNION

          (select
            dst_resource_uuid as resource_uuid,
            dst_ipaddr as ipaddr,
            dst_resource_type as resource_type,
            dst_country_code as country_code,
            dst_asn_num as asn_num,
            exchanged_bytes,
            exchanged_packets
            from ${dbLocations.aggregatedFlowsDb}
          ${whereClause} and (dst_resource_type != 'internet_ip' or src_resource_type = 'internet_ip')
          )
      ) fp
      where resource_uuid is not NULL
      group by resource_uuid
    `;
    console.time(`[flowsRepository] getDashboardData (resourceCount) (fastArquara)`);
    let resourceCounts = await this.executeQueryFastArquaro(this.conn, sqlResourceCount);
    console.timeEnd(`[flowsRepository] getDashboardData (resourceCount) (fastArquara)`);

    let resourceRanking = new Map<string, ResourceFlow>();
    for (let resourceCount of resourceCounts) {
      let flowRessourceObject = NetscopeUtils._produceResourceObject(
        this.nodeMap,
        this.countryMap,
        this.asnMap,
        false,
        resourceCount.ipaddr,
        resourceCount.resource_uuid,
        resourceCount.resource_type,
        "",
        resourceCount.asn_num,
        resourceCount.country_code,
        this.duckDbNodeMapping
      );

      let resourceObject: ResourceFlow = {
        resourceUuid: resourceCount.resource_uuid,
        exchanged_bytes: resourceCount.exchanged_bytes,
        exchanged_packets: resourceCount.exchanged_packets,
        count: resourceCount.count,
        resource: flowRessourceObject
      }
      resourceRanking.set(resourceCount.resource_uuid, resourceObject);
    }

    // Compute resource accessible from outside rankings
    let sqlResourceAccessibleFromOutsideCount = `
      select
        resource_uuid,
        ANY_VALUE(ipaddr) as ipaddr,
        ANY_VALUE(resource_type) as resource_type,
        ANY_VALUE(asn_num) as asn_num,
        ANY_VALUE(country_code) as country_code,
        count(*) as count,
        count(distinct remote_ipaddr) as count_ipaddr,
        count(distinct port) as count_port,
        sum(exchanged_bytes) as exchanged_bytes,
        sum(exchanged_packets) as exchanged_packets
      from
        (
          (select
            src_resource_uuid as resource_uuid,
            src_ipaddr as ipaddr,
            src_resource_type as resource_type,
            src_country_code as country_code,
            src_asn_num as asn_num,
            dst_port as port,
            dst_ipaddr as remote_ipaddr,
            exchanged_bytes,
            exchanged_packets
            from ${dbLocations.flowsDb}
          ${whereClause} and ((src_resource_type != 'internet_ip') and (dst_resource_type = 'internet_ip' and direction = 1))
          )

          UNION

          (select
            dst_resource_uuid as resource_uuid,
            dst_ipaddr as ipaddr,
            dst_resource_type as resource_type,
            dst_country_code as country_code,
            dst_asn_num as asn_num,
            dst_port as port,
            src_ipaddr as remote_ipaddr,
            exchanged_bytes,
            exchanged_packets
            from ${dbLocations.flowsDb}
          ${whereClause} and ((dst_resource_type != 'internet_ip') and (src_resource_type = 'internet_ip' and direction = 0))
          )
      ) fp
      where resource_uuid is not NULL
      group by resource_uuid
    `;
    console.time(`[flowsRepository] getDashboardData (resourceCount) (fastArquara)`);
    let resourceAccessibleFromOutsideCounts = await this.executeQueryFastArquaro(this.conn, sqlResourceAccessibleFromOutsideCount);
    console.timeEnd(`[flowsRepository] getDashboardData (resourceCount) (fastArquara)`);

    let resourceAccessibleFromOutsideRanking = new Map<string, ResourceFlow>();
    for (let resourceAccessibleFromOutsideCount of resourceAccessibleFromOutsideCounts) {
      let flowRessourceObject = NetscopeUtils._produceResourceObject(
        this.nodeMap,
        this.countryMap,
        this.asnMap,
        false,
        resourceAccessibleFromOutsideCount.ipaddr,
        resourceAccessibleFromOutsideCount.resource_uuid,
        resourceAccessibleFromOutsideCount.resource_type,
        "",
        resourceAccessibleFromOutsideCount.asn_num,
        resourceAccessibleFromOutsideCount.country_code,
        this.duckDbNodeMapping
      );

      let resourceAccessibleFromOutsideObject: ResourceFlow = {
        resourceUuid: resourceAccessibleFromOutsideCount.resource_uuid,
        exchanged_bytes: resourceAccessibleFromOutsideCount.exchanged_bytes,
        exchanged_packets: resourceAccessibleFromOutsideCount.exchanged_packets,
        count: resourceAccessibleFromOutsideCount.count,
        countIpaddress: resourceAccessibleFromOutsideCount.count_ipaddr,
        countPort: resourceAccessibleFromOutsideCount.count_port,
        resource: flowRessourceObject
      }
      resourceAccessibleFromOutsideRanking.set(resourceAccessibleFromOutsideCount.resource_uuid, resourceAccessibleFromOutsideObject);
    }

    let result: OutsideFlowsComponentData = {
      outside: {
        amountOfDataProcessed: outsideDashboardValues[0]["amountOfDataProcessed"],
        amountOfPacketsProcessed: outsideDashboardValues[0]["amountOfPacketsProcessed"],
        amountOfFlowsProcessed: outsideDashboardValues[0]["amountOfFlowsProcessed"]
      },
      resourceCount: resourcesCount[0]["count"],
      resourceRanking: resourceRanking,
      resourceAccessibleFromOutsideRanking: resourceAccessibleFromOutsideRanking,
      countryRanking: countryRanking,
      asnRanking: asnRanking,
      vmsWithPortsAccessedFromOutside: []
    };

    return result;
  }

  computeWhereClauseForProtocolAnalysis = (selection: ProtocolAnalysisSelection[]) => {
    let repositoryWhereClause = this.generateWhereClause().replace("where", "");
    let selectionsWhereClause = selection.map((s) => s.whereClause);
    selectionsWhereClause.push(repositoryWhereClause);
    let whereClause = selectionsWhereClause.join(" and ");

    return whereClause;
  }

  getProtocolAnalysisData = async (selection: ProtocolAnalysisSelection[]) => {
    let dbLocations = this.getDatabaseLocations();

    let whereClause = this.computeWhereClauseForProtocolAnalysis(selection);

    let column = selection[selection.length - 1].selectedColumn;

    let sqlQuery = `
      select
        ${column},
        sum(exchanged_bytes) as exchanged_bytes,
        sum(exchanged_packets) as exchanged_packets
          from ${dbLocations.flowsDb}
      where ${whereClause}
      group by ${column}
      order by exchanged_bytes desc
      LIMIT 1000
    `;
    console.time(`[flowsRepository] getProtocolAnalysisData (fastArquara)`);
    let protocolsData = await this.executeQueryFastArquaro(this.conn, sqlQuery);
    console.timeEnd(`[flowsRepository] getProtocolAnalysisData (fastArquara)`);

    let lastMode = "protocol";
    let lastSelectionItem = selection[selection.length - 1];

    switch (lastSelectionItem.selectedColumn) {
      case "protocol":
        lastMode = "protocol";
        break;
      case "src_resource_uuid":
        lastMode = "source";
        break;
      case "dst_resource_uuid":
        lastMode = "destination";
        break;
      case "dst_port":
        lastMode = "port";
        break;
      default:
        lastMode = "root";
    }
    // if (selection.length > 0) {
    // }

    let rootNode: SourceResourceProtocolData = new SourceResourceProtocolData();

    rootNode.object = {
      parent: undefined,
      svg_label: "root",
      svg_id: "root",
      uuid: "root",
      name: "root",
      role: "root",
      subSelection: lastSelectionItem
    }
    rootNode.totalBytes = 0;

    for (let pdata of protocolsData) {
      let newResourceProtocolData = new ResourceProtocolData();
      newResourceProtocolData.resource = new FlowRessourceObject();

      let value = pdata[column];
      let subWhereClause = ` ${column} = '${value}' `;
      if (typeof(value) == "number") {
        subWhereClause = ` ${column} = ${value} `;
      }

      // Process flows resources
      let uuid = `${value}`;
      let name = `${value}`;
      if (column.includes("_uuid")) {
        let resource = this.duckDbNodeMapping.get(value);
        if (resource != undefined) {
          name = resource.name;
        } else {
          name = value.split(":")[1];
        }
      }


      newResourceProtocolData.resource.name = name;
      newResourceProtocolData.resource.uuid = uuid;
      newResourceProtocolData.resource.uuid = uuid;

      let subSelection: ProtocolAnalysisSelection = new ProtocolAnalysisSelection();
      subSelection.whereClause = subWhereClause;
      // if (lastMode == "root") {
      //   subSelection.selectedColumn = "protocol";
      //   subSelection.label = `protocol: ${name}`;
      // }
      if (lastMode == "protocol" || lastMode == "port") {
        subSelection.selectedColumn = "src_resource_uuid";
        subSelection.label = `${lastMode}: ${name}`;
      }
      if (lastMode == "source") {
        subSelection.selectedColumn = "dst_resource_uuid";
        subSelection.label = `source: ${name}`;
      }
      if (lastMode == "destination") {
        subSelection.selectedColumn = "dst_resource_uuid";
        subSelection.label = `destination: ${name}`;
      }

      if (lastMode == "protocol" && value == "unknown") {
        subSelection.selectedColumn = "dst_port";
      }

      newResourceProtocolData.object = {
        name: name,
        svg_label: value,
        svg_id: uuid,
        role: "protocol",
        uuid: uuid,
        parent: undefined,
        subSelection: subSelection
      }

      newResourceProtocolData.totalBytes = pdata.exchanged_bytes;
      rootNode.destinations.set(value, newResourceProtocolData);

      rootNode.totalBytes += pdata.exchanged_bytes;
    }

    return rootNode;
  }

  close = () => {
    this.conn.close();
  }

  async getAllFlowsForCsvExport(perPort=false) : Promise<FlowDetail[]> {
    let dbLocations = this.getDatabaseLocations();

    let exchangedBytesColumn = "exchanged_bytes";
    let exchangedPacketsColumn = "exchanged_packets";
    let additionalColumns = "";

    let table = dbLocations.aggregatedFlowsDb;

    if (perPort) {
      exchangedBytesColumn = "exchanged_bytes as exchanged_bytes";
      exchangedPacketsColumn = "exchanged_packets as exchanged_packets";
      additionalColumns = `
        dst_port as port,
        direction,
        protocol,
        proto_raw,
      `;
      table = dbLocations.flowsDb;
    }

    let whereClause = this.generateWhereClause();
    let sortClause = `order by exchanged_bytes DESC`;

    let fetchAllFlowsQuery = `
        select
          src_ipaddr,
          src_resource_uuid,
          src_resource_type,
          dst_ipaddr,
          dst_resource_uuid,
          dst_resource_type,
          ${additionalColumns}
          ${exchangedPacketsColumn},
          ${exchangedBytesColumn},
          src_country_code,
          src_asn_num,
          dst_country_code,
          dst_asn_num
        from ${table}
        ${whereClause}
        ${sortClause}
    `;

    console.time("[flowsRepository] getAllFlowsForCsvExport (fastArquara)");
    let allFlowsWithPortsAndProtocolsFastBatch = await this.executeQueryFastArquaro(this.conn, fetchAllFlowsQuery);
    console.timeEnd("[flowsRepository] getAllFlowsForCsvExport (fastArquara)");

    console.time("[flowsRepository] getAllFlowsForCsvExport (processing links)");
    let result = NetscopeUtils.duckDbFlowsToFlowsDetails(allFlowsWithPortsAndProtocolsFastBatch, this.nodeMap, this.countryMap, this.asnMap, this.duckDbNodeMapping, false, false);
    console.timeEnd("[flowsRepository] getAllFlowsForCsvExport (processing links)");

    return result;
  }

  async getSrcTargetResourceTypesData() : Promise<SrcTargetResourceTypeData[]> {
    let dbLocations = this.getDatabaseLocations();
    let table = dbLocations.aggregatedFlowsDb;

    let whereClause = this.generateWhereClause();
    let sortClause = `order by exchanged_bytes DESC`;

    let srcTargetResourceTypesDataQuery = `
        select
          src_resource_type,
          dst_resource_type,
          sum(exchanged_bytes) as exchanged_bytes,
          sum(exchanged_packets) as exchanged_packets,
          count(*) as count
        from ${table}
        ${whereClause}
          group by src_resource_type, dst_resource_type
        ${sortClause}
    `;

    console.time("[flowsRepository] getAllFlowsForCsvExport (fastArquara)");
    let srcTargetResourceTypesData = await this.executeQueryFastArquaro(this.conn, srcTargetResourceTypesDataQuery);
    console.timeEnd("[flowsRepository] getAllFlowsForCsvExport (fastArquara)");

    // console.time("[flowsRepository] getAllFlowsForCsvExport (processing links)");
    // let result = NetscopeUtils.duckDbFlowsToFlowsDetails(allFlowsWithPortsAndProtocolsFastBatch, this.nodeMap, this.countryMap, this.asnMap, this.duckDbNodeMapping, false, false);
    // console.timeEnd("[flowsRepository] getAllFlowsForCsvExport (processing links)");
    let result = srcTargetResourceTypesData;
    return result;
  }

  async getNetworkViewerData() : Promise<NetworkViewerData[]> {
    let dbLocations = this.getDatabaseLocations();
    let table = dbLocations.aggregatedFlowsDb;

    let whereClause = this.generateWhereClause();
    let sortClause = `order by exchanged_bytes DESC`;

    let srcTargetResourceTypesDataQuery = `
        select
          src_ipaddr as src_ipaddr,
          src_resource_type as src_resource_type,
          dst_ipaddr as dst_ipaddr,
          dst_resource_type as dst_resource_type,
          src_country_code as src_country_code,
          src_asn_num as src_asn_num,
          dst_country_code as dst_country_code,
          dst_asn_num as dst_asn_num,

          sum(exchanged_packets) as exchanged_packets,
          sum(exchanged_bytes) as exchanged_bytes

        from ${table}
        ${whereClause}
          group by src_ipaddr, src_resource_type, dst_ipaddr, dst_resource_type, src_country_code, src_asn_num, dst_country_code, dst_asn_num
        ${sortClause}
    `;

    console.time("[flowsRepository] getAllFlowsForCsvExport (fastArquara)");
    let srcTargetResourceTypesData = await this.executeQueryFastArquaro(this.conn, srcTargetResourceTypesDataQuery);
    console.timeEnd("[flowsRepository] getAllFlowsForCsvExport (fastArquara)");

    // console.time("[flowsRepository] getAllFlowsForCsvExport (processing links)");
    // let result = NetscopeUtils.duckDbFlowsToFlowsDetails(allFlowsWithPortsAndProtocolsFastBatch, this.nodeMap, this.countryMap, this.asnMap, this.duckDbNodeMapping, false, false);
    // console.timeEnd("[flowsRepository] getAllFlowsForCsvExport (processing links)");
    let result = srcTargetResourceTypesData;
    return result;
  }

  buildAsnsAndCountriesMaps = async (): Promise<AsnCountryMaps> => {
      console.log("connecting to embedded database");
      let dbLocations = this.getDatabaseLocations();
      // const conn = await db.connect();

      // Query
      console.log("duckdb: query1");
      let allCountries: Array<CountryEntry> = await this.executeQueryFastArquaro(this.conn, `
        select country, countryCode from (
          select src_country as country, src_country_code as countryCode
          from ${dbLocations.flowsDb}
          ${ this.whereClause }
          group by src_country, src_country_code

          UNION

          select dst_country as country, dst_country_code as countryCode
          from ${dbLocations.flowsDb}
          ${ this.whereClause }
          group by dst_country, dst_country_code
        )
      `);
      console.log(`allCountries.length ${allCountries.length} countries`);

      // Query
      console.log("duckdb: query2");
      let allAsns: Array<AsnEntry> = await this.executeQueryFastArquaro(this.conn, `
        select asn, asnNum from (
          select src_asn_name as asn, src_asn_num as asnNum
          from ${dbLocations.flowsDb}
          ${ this.whereClause }
          group by asn, asnNum

          UNION

          select dst_asn_name as asn, dst_asn_num as asnNum
          from ${dbLocations.flowsDb}
          ${ this.whereClause }
          group by asn, asnNum
        )
      `);
      console.log(`allAsns.length ${allAsns.length} asns`);

      // CountryMap
      let countryMap: Map<string, CountryEntry> = new Map<string, CountryEntry>();
      for (let country of allCountries) {
        if (country.country === null || country.countryCode === null) {
          console.warn("Some countries have null fields : this can be cause by some inconsistencies in the ASN/Countries public database");
        } else {
            countryMap.set(country.countryCode, country);
        }
      }
      console.log(`countryMap.length ${countryMap.size} entries`);

      // AsnMap
      let asnMap: Map<number, AsnEntry> = new Map<number, AsnEntry>();
      for (let asn of allAsns) {
        if (asn.asn === null || asn.asnNum === null) {
          console.warn("Some asn have null fields : this can be cause by some inconsistencies in the ASN/Countries public database");
        } else {
            asnMap.set(asn.asnNum, asn);
        }
      }
      console.log(`asnMap.length ${asnMap.size} entries`);

      let result: AsnCountryMaps = {
        asnMap: asnMap,
        countryMap: countryMap
      };

      return result;
  }

  computeProtocolsRatio = async (): Promise<DuckDbProtocolRatioDescription[]> => {
      console.log("duckdb: query5");
      let dbLocations = this.getDatabaseLocations();
      const protocolRatio = await this.executeQueryFastArquaro(this.conn, `
        select protocol, sum(exchanged_bytes) as protocol_total_bytes
        from ${dbLocations.flowsDb}
        ${ this.whereClause }
        group by protocol
        order by protocol_total_bytes DESC`);
      console.log(`protocolRatio.length: ${protocolRatio.length}`);

      return protocolRatio;
  }

  computeTopEmitters = async (): Promise<DuckDbEmitterDescription[]> => {
      console.log("duckdb: query6");
      let dbLocations = this.getDatabaseLocations();
      const topEmitters = await this.executeQueryFastArquaro(this.conn, `
        select src_resource_uuid, ANY_VALUE(src_ipaddr) as src_ipaddr, sum(source_total_bytes) as source_total_bytes
        from (
          select IF(src_resource_uuid != 'unknown', src_resource_uuid, CONCAT('vim.UnknownIpaddress:', src_ipaddr)) as src_resource_uuid, ANY_VALUE(src_ipaddr) as src_ipaddr, sum(exchanged_bytes) as source_total_bytes
          from ${dbLocations.aggregatedFlowsDb}
          ${ this.whereClause }
          group by src_resource_uuid, src_ipaddr
        )
        group by src_resource_uuid
        order by source_total_bytes DESC
        LIMIT 100`);
      console.log(`topEmitters.length: ${topEmitters.length}`);

      return topEmitters;
  }
}

////////////////////////////////////////
//
// FLOW REPOSITORY SEARCH
//
//
//
//
////////////////////////////////////////

export class FlowRepositorySearch {

  flowRepository: FlowsRepository;
  appsGroups: Array<DuckDbAppsGroup>;

  comboboxSelectionItems: ComboboxSelectionItem[] = [];

  constructor(flowRepository: FlowsRepository, appsGroups: DuckDbAppsGroup[]) {
    this.flowRepository = flowRepository;
    this.appsGroups = appsGroups;
  }

  async init(columns: string[], possibleActionsFromArgs: string[]=undefined) {
    this.comboboxSelectionItems = [];

    let possibleActions = ["focus", "ignore"];
    if (possibleActionsFromArgs != undefined && possibleActionsFromArgs.length > 0) {
      possibleActions = possibleActionsFromArgs;
    }

    let resourceIndex: Map<string, DCNetscopeResourceWithName> = new Map<string, DCNetscopeResourceWithName>();

    // Resources
    let resourceArgs = [];
    if (columns.length == 0 || columns.includes("source")) {
      resourceArgs.push(["src_resource_uuid", "src_ipaddr"]);
    }
    if (columns.length == 0 || columns.includes("destination")) {
      resourceArgs.push(["dst_resource_uuid", "dst_ipaddr"]);
    }

    for (let [uuid_column, ip_collum] of resourceArgs) {
      let discreteValues = await this.flowRepository.getAllDiscreteValues([uuid_column, ip_collum]);

      let allResources = discreteValues.map((respObj) => {
        let resourceUuid = respObj[uuid_column];
        let resourceIpaddress = respObj[ip_collum];

        let resourceObject: DCNetscopeResourceWithName;
        if (this.flowRepository.duckDbNodeMapping.has(resourceUuid)) {
          let resourceObjectFromMapping = this.flowRepository.duckDbNodeMapping.get(resourceUuid);
          resourceObject = new DCNetscopeResourceWithName(resourceUuid, resourceObjectFromMapping.name);
          resourceObject.address = resourceIpaddress;
          resourceObject.name = resourceObject.name + " (" + resourceIpaddress + ")";
        } else {
          let resourceUuid = `vim.UnknownIpaddress:${resourceIpaddress}`;
          let resourceObjectName = resourceIpaddress;
          resourceObject = new DCNetscopeResourceWithName(resourceUuid, resourceObjectName);
          resourceObject.address = resourceIpaddress;
        }

        return resourceObject;
      })

      for (let resource of allResources) {
        if (resourceIndex.has(resource.uuid)) {
          continue;
        }

        for (let action of possibleActions) {
          let newcomboboxSelectionItem = new ComboboxSelectionItem(resource, action);
          this.comboboxSelectionItems.push(newcomboboxSelectionItem);
        }

        resourceIndex.set(resource.uuid, resource);
      }
    }

    // protocols
    if (columns.length == 0 || columns.includes("protocols")) {
      let protocolsDiscreteValues = await this.flowRepository.getAllDiscreteValues(["protocol"]);
      for (let protocolObject of protocolsDiscreteValues) {
        let protocol = protocolObject.protocol;
        let protocolResource = new DCNetscopeResourceWithName(protocol, protocol);
        let newcomboboxSelectionItem = new ComboboxSelectionItem(protocolResource, "protocol");
        this.comboboxSelectionItems.push(newcomboboxSelectionItem);
      }
    }

    // apps groups
    if (columns.length == 0 || columns.includes("appgroups")) {
      for (let appGroup of this.appsGroups) {
        let newcomboboxSelectionItem = new ComboboxSelectionItem(appsGroupFilterResource(appGroup), "appgroup");
        this.comboboxSelectionItems.push(newcomboboxSelectionItem);
      }
    }
  }

  generateManualSearchItem = (itemName: string) => {

    let resourceType = "ip"
    if (itemName.includes("/")) {
      resourceType = "network";
    }
    let resource = new DCNetscopeResourceWithName(`${resourceType}:${itemName}`, itemName);

    let item = new ComboboxSelectionItem(resource, "focus");

    // TODO: it would be good if the following value was not undefined
    item.initManualSqlWhereClause(undefined);

    return item;
  }

  search(filter: string): ComboboxSelectionItem[] {
    console.time("searchFilter");
    let maxResults = 30;
    let lowerCaseFilter = filter.toLowerCase();
    let matchingResults = this.comboboxSelectionItems
      .filter((c) => {
        if (c.resource.name && c.resource.name.toLowerCase().includes(lowerCaseFilter)) {
          return true;
        }
        if (c.resource.address && c.resource.address.includes(lowerCaseFilter)) {
          return true;
        }
        return false;
      });
    let narrowedResults = matchingResults.splice(0, maxResults);

    if (narrowedResults.length == 0) {
      narrowedResults.push(this.generateManualSearchItem(filter));
    }

    return narrowedResults;
  }
}

////////////////////////////////////////
//
// FlowsRepositoryWithBrowserDuckDB
//
//
//
//
////////////////////////////////////////

export class FlowsRepositoryWithBrowserDuckDB extends FlowsRepository {

  async executeQueryFastArquaro(conn, query) {
    console.log("debug 0");
    // const result0 = await conn.query(query);
    let result = [];

    // Set the arrow batch size (in rows)
    await conn.query(`SET preserve_insertion_order=false`);

    const fetchFlowsAndSubflowsQueryResult = await conn.query(query);
    let flowAndSubflowsTable = tableToIPC(fetchFlowsAndSubflowsQueryResult);

    console.log("executeQueryFastArquaro (1)");
    let arqueroFlowAndSubflowsTable: ColumnTable = aq.fromArrow(flowAndSubflowsTable);
    console.log("executeQueryFastArquaro (2)");
    result = arqueroFlowAndSubflowsTable.objects();
    console.log("executeQueryFastArquaro (3)");

    return result;
  }

  clone = (): FlowsRepository => {
    let newRepository = new FlowsRepositoryWithBrowserDuckDB(this.conn, this.whereClause, this.nodeMap, this.countryMap, this.asnMap, this.duckDbNodeMapping);
    newRepository.valuesForFilters = this.valuesForFilters;
    newRepository.initWhereClause = this.initWhereClause;
    newRepository.aggregated_flows_files = this.aggregated_flows_files;
    newRepository.flows_files = this.flows_files;
    newRepository.flowsSubsetClause = this.flowsSubsetClause;
    return newRepository;
  }
}

export class FlowsRepositoryWithRemoteDuckDB extends FlowsRepository {

  rawParquetPath: string;
  rawAggregatedParquetPath: string;

  constructor(private apiService: NetscopeApiService, conn: duckdb.AsyncDuckDBConnection, whereClause: string, nodeMap: Map<string, FlowRessourceObject>, countryMap: Map<string, CountryEntry>, asnMap: Map<number, AsnEntry>, duckDbNodeMapping: Map<string, DuckDbResourceObject>, tmp_parquet_filepath: string, aggregated_tmp_parquet_filepath: string) {
    super(conn, whereClause, nodeMap, countryMap, asnMap, duckDbNodeMapping);
    this.rawParquetPath = tmp_parquet_filepath;
    this.rawAggregatedParquetPath = aggregated_tmp_parquet_filepath;
  }

  override getDatabaseLocations = () => ({
    "raw": `read_parquet('${this.rawParquetPath}')`,
    "aggregatedRaw": `read_parquet('${this.rawAggregatedParquetPath}')`,
    "flowsDb": this.subsetOf(`read_parquet('${this.flows_files}', hive_partitioning = true)`),
    "aggregatedFlowsDb": this.subsetOf(`read_parquet('${this.aggregated_flows_files}', hive_partitioning = true)`),
  })

  override generateNewFilepaths = () => {
    let internalUuid = NetscopeUtils.generateUuid();

    let flows_files = `${internalUuid}_file`;
    let aggregated_flows_files = `${internalUuid}_file_aggregated`;

    return {
      internalUuid: internalUuid,
      flows_files: `/root/sessions/${this.apiService.sessionUuid}/tmp_${flows_files}`,
      aggregated_flows_files: `/root/sessions/${this.apiService.sessionUuid}/tmp_${aggregated_flows_files}`,
    }
  }

  async executeQueryFastArquaro(conn, query): Promise<any[]>  {
    const queryResponse = await firstValueFrom(
      this.apiService.executeQueryRemotely(query, "")
      // this.apiService.executeQueryRemotelyArrow(query, "")
      // this.apiService.executeQueryRemotelyParquet(query, "")
    );

    let result: any[] = [];
    if (queryResponse instanceof Success) {
      // @ts-ignore
      result = queryResponse.element;
    }

    // console.log(`${query} => \n\n${result}`);

    return result;
  }

  clone = (): FlowsRepository => {
    let newRepository = new FlowsRepositoryWithRemoteDuckDB(this.apiService, this.conn, this.whereClause, this.nodeMap, this.countryMap, this.asnMap, this.duckDbNodeMapping, this.rawParquetPath, this.rawAggregatedParquetPath);
    newRepository.valuesForFilters = this.valuesForFilters;
    newRepository.initWhereClause = this.initWhereClause;
    newRepository.aggregated_flows_files = this.aggregated_flows_files;
    newRepository.flows_files = this.flows_files;
    newRepository.flowsSubsetClause = this.flowsSubsetClause;
    // newRepository.internalUuid = this.internalUuid;
    return newRepository;
  }
}
